/**
 * The message exchange between the app and its sandboxed Mermaid frame (openspec change
 * `artefacts-viewer`, design §4 and task 2.7a). The frame runs in an opaque origin
 * (`sandbox="allow-scripts"`, never `allow-same-origin`), so messages are its only channel:
 *
 * - the parent posts `{ type: "esi:mermaid:render", nonce, source }` to the frame it created;
 * - the frame accepts it only from its parent window, and answers that window alone, at the
 *   origin the request came from, with `{ type: "esi:mermaid:result", nonce, svg }` or
 *   `{ type: "esi:mermaid:result", nonce, error: true }` (never Mermaid's message: it can quote
 *   the source);
 * - the parent accepts an answer only from that frame's window, from an opaque origin, carrying
 *   the request's nonce.
 *
 * The frame never reads a diagram from its URL (hash or query) or from storage, and opened
 * outside a frame, or framed without the sandbox (a real origin), it draws nothing. Pure: no Mermaid here, so the app bundle never includes it.
 */

export const RENDER_REQUEST = "esi:mermaid:render";
export const RENDER_RESULT = "esi:mermaid:result";

/** Same bound as an artefact's content (ARTEFACT_LIMITS.contentChars). */
export const MAX_SOURCE_CHARS = 100_000;
/** The most edges a diagram may draw (Mermaid's `maxEdges`): the frame's complexity bound. */
export const MAX_EDGES = 500;
/** The largest SVG text the parent takes back from the frame. */
export const MAX_SVG_CHARS = 4_000_000;

const NONCE = /^[A-Za-z0-9_-]{22,64}$/;

/**
 * Keys a diagram's own `%%{init}%%` directive or front matter may never set, on top of Mermaid's
 * `secure` list: `htmlLabels` (HTML labels are drawn in `foreignObject`, which the browser's SVG
 * check strips, leaving a diagram without labels) and `themeCSS`.
 */
export const MERMAID_SECURE_KEYS = ["htmlLabels", "themeCSS"] as const;

/** A drawing that still holds HTML labels: they would be stripped, so it counts as failed. */
export function hasForeignContent(svg: string): boolean {
  return /<foreignObject\b/i.test(svg);
}

export interface RenderRequest {
  type: typeof RENDER_REQUEST;
  nonce: string;
  source: string;
}

export type RenderResult =
  | { type: typeof RENDER_RESULT; nonce: string; svg: string }
  | { type: typeof RENDER_RESULT; nonce: string; error: true };

/** A fresh, unguessable request nonce (128 bits, base64url). */
export function newNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Whether `win` is a top-level window (no parent frame): the frame then stays inert. */
export function isTopLevel(win: Window): boolean {
  return win.parent === win || win.top === win;
}

/**
 * Whether the frame may draw: framed, and in an opaque origin (`sandbox` without
 * `allow-same-origin`). A page framing it without the sandbox would give it ESI's real origin:
 * it then stays inert, whatever the framing policy in force.
 */
export function maySandboxRender(win: Window): boolean {
  return !isTopLevel(win) && win.origin === "null";
}

/**
 * The frame's check of an incoming message: from its parent window only, of the exact shape,
 * with a nonce and a bounded source. Anything else is ignored (null).
 */
export function acceptRequest(event: MessageEvent, win: Window): RenderRequest | null {
  if (!maySandboxRender(win) || event.source !== win.parent) {
    return null;
  }
  const data = event.data as Partial<RenderRequest> | null;
  if (
    typeof data !== "object" ||
    data === null ||
    data.type !== RENDER_REQUEST ||
    typeof data.nonce !== "string" ||
    !NONCE.test(data.nonce) ||
    typeof data.source !== "string" ||
    data.source.length > MAX_SOURCE_CHARS
  ) {
    return null;
  }
  return { type: RENDER_REQUEST, nonce: data.nonce, source: data.source };
}

/**
 * The parent's check of an answer: from the frame it created (`frame`), from an opaque origin,
 * carrying the request's nonce. Returns the SVG text, `"error"` (a failed render, or a drawing
 * that is too large or still holds HTML labels), or null (not this answer).
 */
export function acceptResult(
  event: MessageEvent,
  frame: Window | null,
  nonce: string,
): { svg: string } | "error" | null {
  if (frame === null || event.source !== frame || event.origin !== "null") {
    return null;
  }
  const data = event.data as Partial<{ type: string; nonce: string; svg: unknown; error: unknown }>;
  if (typeof data !== "object" || data === null || data.type !== RENDER_RESULT) {
    return null;
  }
  if (data.nonce !== nonce) {
    return null;
  }
  if (
    typeof data.svg === "string" &&
    data.svg.length <= MAX_SVG_CHARS &&
    !hasForeignContent(data.svg)
  ) {
    return { svg: data.svg };
  }
  return "error";
}

/**
 * The frame's side: answers each render request from its parent with `render`'s SVG, one at a
 * time. Opened top-level or outside a sandbox, it registers nothing and calls `inert` (a plain
 * notice).
 */
export function startFrame(
  win: Window,
  render: (source: string) => Promise<string>,
  inert: () => void,
): (() => void) | null {
  if (!maySandboxRender(win)) {
    inert();
    return null;
  }
  let queue: Promise<unknown> = Promise.resolve();
  const onMessage = (event: MessageEvent) => {
    const request = acceptRequest(event, win);
    if (!request) {
      return;
    }
    // The parent's origin, as the browser stamped it: the answer goes to it alone.
    const origin = event.origin;
    queue = queue.then(async () => {
      let result: RenderResult;
      try {
        const svg = await render(request.source);
        if (hasForeignContent(svg)) {
          throw new Error("HTML labels");
        }
        result = { type: RENDER_RESULT, nonce: request.nonce, svg };
      } catch {
        result = { type: RENDER_RESULT, nonce: request.nonce, error: true };
      }
      try {
        win.parent.postMessage(result, origin);
      } catch {
        // An origin the browser cannot target (an opaque parent): nobody to answer.
      }
    });
  };
  win.addEventListener("message", onMessage);
  return () => win.removeEventListener("message", onMessage);
}
